import { Children, useCallback, useMemo, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import { getColumnId } from './utils/index.js';
import { DataTableColumnDefinition, DataTableRow } from './DataTableTypes.js';

export type DataTableColumnsDraggableProps<TRow extends DataTableRow, TContext = unknown> = {
  children: React.ReactElement[];
  columns: DataTableColumnDefinition<TRow, TContext>[];
  controlled?: boolean;
  onColumnSort?: (event: DragMoveEvent | DragEndEvent, columnOrder: string[]) => void;
  reorderItems?: typeof arrayMove;
};

export const DataTableColumnsDraggable = <TRow extends DataTableRow, TContext = unknown>({
  columns,
  onColumnSort,
  reorderItems = arrayMove,
  controlled,
  children,
}: DataTableColumnsDraggableProps<TRow, TContext>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const columnIds = useMemo(() => columns.map((column) => getColumnId(column)), [columns]);

  const [stateColumnIds, setStateColumnIds] = useState(columnIds);

  const actualColumnIds = controlled ? columnIds : stateColumnIds;

  const [activeId, setActiveId] = useState<ElementType<typeof columnIds> | null>(null);

  const getIndex = columnIds.indexOf.bind(columnIds);
  // const getPosition = (id: string) => getIndex(id as ElementType<typeof ids>) + 1;
  const activeIndex = activeId ? getIndex(activeId) : -1;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as ElementType<typeof columnIds>);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { over } = event;
      setActiveId(null);

      if (over) {
        const overIndex = getIndex(over.id as ElementType<typeof columnIds>);
        if (activeIndex !== overIndex) {
          const reorderedItems = reorderItems(actualColumnIds, activeIndex, overIndex);
          onColumnSort?.(event, reorderedItems);
          if (!controlled) {
            setStateColumnIds(reorderedItems);
          }
        }
      }
    },
    [activeIndex, actualColumnIds, controlled, getIndex, onColumnSort, reorderItems]
  );

  // // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars, @typescript-eslint/no-unused-vars
  // function handleDragMove(event: DragMoveEvent) {
  //   const { over } = event;
  //   if (over) {
  //     const overIndex = getIndex(over.id as ElementType<typeof ids>);
  //     if (activeIndex !== overIndex) {
  //       onColumnSort(event, reorderItems(ids, activeIndex, overIndex));
  //     }
  //   }
  // }

  const columnNodes = Children.toArray(children) as typeof children;

  const activeColumn = useMemo(
    () => (activeId ? columnNodes.find(({ props }) => activeId === props.field) : null),
    [activeId, columnNodes]
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      // onDragMove={handleDragMove}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={actualColumnIds} strategy={horizontalListSortingStrategy}>
        {columnNodes}
      </SortableContext>
      <DragOverlay>{activeColumn}</DragOverlay>
    </DndContext>
  );
};
